import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Switch, message, Divider, Row, Col, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { fornecedorService } from '../services/api';
import ShortId from './common/ShortId';

const FornecedoresList = () => {
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchFornecedores();
  }, []);

  const fetchFornecedores = async () => {
    setLoading(true);
    try {
      const response = await fornecedorService.list();
      setFornecedores(response.data);
    } catch (error) {
      message.error('Erro ao carregar fornecedores');
      console.error(error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      nome: record.nome,
      documento: record.documento,
      email: record.email,
      telefone: record.telefone,
      is_ativo: !!record.is_ativo,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir este fornecedor?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await fornecedorService.delete(id);
          message.success('Fornecedor excluído com sucesso');
          fetchFornecedores();
        } catch (error) {
          message.error('Erro ao excluir fornecedor');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        nome: values.nome,
        documento: values.documento || null,
        email: values.email || null,
        telefone: values.telefone || null,
        is_ativo: !!values.is_ativo,
      };

      if (editingId) {
        await fornecedorService.update(editingId, payload);
        message.success('Fornecedor atualizado com sucesso');
      } else {
        await fornecedorService.create(payload);
        message.success('Fornecedor criado com sucesso');
      }
      setIsModalVisible(false);
      fetchFornecedores();
    } catch (error) {
      message.error('Erro ao salvar fornecedor');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (id) => <ShortId id={id} />,
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Documento',
      dataIndex: 'documento',
      key: 'documento',
      width: 150,
    },
    {
      title: 'Contato',
      key: 'contato',
      render: (_, record) => (
        <div>
          {record.telefone && <div>{record.telefone}</div>}
          {record.email && <div>{record.email}</div>}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_ativo',
      key: 'is_ativo',
      width: 80,
      render: (isAtivo) => (
        <Tag color={isAtivo ? 'green' : 'red'}>
          {isAtivo ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Novo Fornecedor
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={fornecedores}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingId ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_ativo: true }}
        >
          <Divider>Dados Básicos</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={24}>
              <Form.Item
                name="nome"
                label="Nome"
                rules={[{ required: true, message: 'Campo obrigatório' }]}
              >
                <Input placeholder="Nome do fornecedor" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="documento" label="Documento">
                <Input placeholder="CPF/CNPJ ou outro documento" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="is_ativo" label="Status" valuePropName="checked">
                <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Contato</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="telefone" label="Telefone">
                <Input placeholder="(00) 0000-0000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                <Input placeholder="email@exemplo.com" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default FornecedoresList;
